import {
  useCallback, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { MAX_GROUP_COUNT } from '~/_shared/constants/grouping.constants';
import type { SpreadsheetColumnId } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import { useTranslation } from '~/_shared/utils/hooks';
import { isNullsy } from '~/_shared/utils/typeGuards';
import { AppErrorType } from '~/appError/appErrorType.enum';
import { createAppError } from '~/store/modal/modal.actionCreators';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { getColumnAttributeUniqueAttributesCount } from '~/store/spreadsheetData/filtering/spreadsheetDataFiltering.helpers';
import { getSpreadsheetDataForRequestedColumnTypes } from '~/store/spreadsheetData/spreadsheetData.helpers';
import { DataType } from '~/store/spreadsheetData/spreadsheetData.state';

export const useAttributeTypeColumnValidator = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [t] = useTranslation();
  const clientId = useClientIdSelector();
  const mapId = useMapIdSelector();

  const validate = useCallback(async (spreadsheetColumn: SpreadsheetColumnId) => {
    if (isNullsy(clientId) || isNullsy(mapId)) {
      return false;
    }

    setIsLoading(true);
    try {
      const spreadsheetData = await getSpreadsheetDataForRequestedColumnTypes(
        clientId,
        mapId,
        spreadsheetColumn,
        [DataType.ATTRIBUTE],
      );
      const uniqueGroupsCount = getColumnAttributeUniqueAttributesCount(spreadsheetData, spreadsheetColumn) ?? 0;

      if (uniqueGroupsCount > MAX_GROUP_COUNT) {
        dispatch(createAppError({
          errorTitle: t('filterTool.group.tooManyValues.solution'),
          text: t('filterTool.group.tooManyValues.description', { MAX_GROUP_COUNT }),
          title: t('Invalid Column for Grouping'),
          type: AppErrorType.General,
        }));

        return false;
      }

      return true;
    }
    catch (e) {
      console.error(e);
      return false;
    }
    finally {
      setIsLoading(false);
    }

  }, [clientId, dispatch, mapId, t]);

  return {
    validateAttributeColumn: validate,
    isLoading,
  };
};
